import {
  useEffect,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from "react";
import { usePresentationStore } from "@/stores/presentation-store";
import type { PresentationState } from "@/lib/focus/focus-types";
import { useFrameField } from "@/stores/frames-selectors";
import { useFrameRenderer } from "./frame-renderer-context";
import { useInlineFrameEmbed } from "./inline-frame-embed-context";

interface FrameEditSnapshot {
  available: boolean;
  editing: boolean;
  saving: boolean;
  beginEditing: () => void;
  saveEdits: () => void | Promise<void>;
}

/** `focus: false` enters edit mode without moving keyboard focus. */
type BeginFrameEditing = (options: { focus: boolean }) => void;

interface FrameEditRegistration {
  snapshot: FrameEditSnapshot;
  beginEditingRef: { current: BeginFrameEditing };
  saveEditsRef: { current: () => void | Promise<void> };
}

interface FrameEditControllerOptions {
  enabled?: boolean;
  editing: boolean;
  saving?: boolean;
  /** Stay in edit mode while an agent Focus shows this frame; edits autosave. */
  editInAgentFocus?: boolean;
  beginEditing: BeginFrameEditing;
  saveEdits: () => void | Promise<void>;
}

const EMPTY_SNAPSHOT: FrameEditSnapshot = {
  available: false,
  editing: false,
  saving: false,
  beginEditing: () => undefined,
  saveEdits: () => undefined,
};

const registrations = new Map<string, FrameEditRegistration>();
const listeners = new Map<string, Set<() => void>>();

function emit(frameId: string) {
  listeners.get(frameId)?.forEach((listener) => listener());
}

function subscribe(frameId: string, listener: () => void) {
  const frameListeners = listeners.get(frameId) ?? new Set<() => void>();
  frameListeners.add(listener);
  listeners.set(frameId, frameListeners);
  return () => {
    frameListeners.delete(listener);
    if (frameListeners.size === 0) listeners.delete(frameId);
  };
}

function getSnapshot(frameId: string) {
  return registrations.get(frameId)?.snapshot ?? EMPTY_SNAPSHOT;
}

/** Whether the frame is in its native edit mode right now. */
export function isFrameEditing(frameId: string): boolean {
  return getSnapshot(frameId).editing;
}

function shownInAgentFocus(
  presentation: PresentationState | null,
  frameId: string,
): boolean {
  return (
    presentation?.variant === "focus" &&
    !!presentation.origin &&
    presentation.frameIds.includes(frameId)
  );
}

/**
 * Register a frame's native edit lifecycle with the universal hover chrome.
 * Inline embed copies never register: the canvas node owns the frame id.
 */
export function useRegisterFrameEditController(
  frameId: string,
  {
    enabled = true,
    editing,
    saving = false,
    editInAgentFocus = false,
    beginEditing,
    saveEdits,
  }: FrameEditControllerOptions,
) {
  const inlineEmbed = useInlineFrameEmbed();
  const renderer = useFrameRenderer();
  const { focused } = renderer.useChromeState(frameId);
  const windowed = renderer.closeFrame !== undefined;
  const registers = enabled && !inlineEmbed;
  // A just-created frame paints as streaming for its first second.
  const streaming = useFrameField(
    frameId,
    (frame) => frame?.status === "streaming",
  );
  const agentFocusEditing = usePresentationStore(
    (state) =>
      registers &&
      editInAgentFocus &&
      focused &&
      !windowed &&
      !streaming &&
      shownInAgentFocus(state.presentation, frameId),
  );
  const agentFocusEditStartedRef = useRef(false);
  const beginEditingRef = useRef(beginEditing);
  const saveEditsRef = useRef(saveEdits);
  const initialEditingRef = useRef(editing);
  const initialSavingRef = useRef(saving);
  beginEditingRef.current = beginEditing;
  saveEditsRef.current = saveEdits;

  useLayoutEffect(() => {
    if (!registers) return;
    const registration: FrameEditRegistration = {
      beginEditingRef,
      saveEditsRef,
      snapshot: {
        available: true,
        editing: initialEditingRef.current,
        saving: initialSavingRef.current,
        beginEditing: () =>
          registration.beginEditingRef.current({ focus: true }),
        saveEdits: () => registration.saveEditsRef.current(),
      },
    };
    registrations.set(frameId, registration);
    emit(frameId);
    return () => {
      if (registrations.get(frameId) !== registration) return;
      registrations.delete(frameId);
      emit(frameId);
    };
  }, [registers, frameId]);

  useLayoutEffect(() => {
    const registration = registrations.get(frameId);
    if (
      !registers ||
      !registration ||
      (registration.snapshot.editing === editing &&
        registration.snapshot.saving === saving)
    ) {
      return;
    }
    registration.snapshot = { ...registration.snapshot, editing, saving };
    emit(frameId);
  }, [editing, registers, frameId, saving]);

  // Agent Focus keeps the frame editable: leaving edit mode (an outside
  // click saves) re-enters it, and the Focus ending saves and exits.
  useEffect(() => {
    if (agentFocusEditing) {
      if (editing || saving) return;
      agentFocusEditStartedRef.current = true;
      beginEditingRef.current({ focus: false });
      return;
    }
    if (!agentFocusEditStartedRef.current) return;
    agentFocusEditStartedRef.current = false;
    if (editing) void saveEditsRef.current();
  }, [agentFocusEditing, editing, saving]);
}

/** Read and invoke the native edit lifecycle registered by a frame. */
export function useFrameEditController(frameId: string): FrameEditSnapshot {
  return useSyncExternalStore(
    (listener) => subscribe(frameId, listener),
    () => getSnapshot(frameId),
    () => EMPTY_SNAPSHOT,
  );
}
